import React, { useEffect, useState } from 'react';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from "react-i18next";
import { FormGroup, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import AppInput from "components/Input/AppInput";
import AppButton from "components/CustomButtons/AppButton";
import { YupValidations } from 'utils/validate';
import { useHistory, useParams } from 'react-router';
import SelectField from 'components/Select/SelectField';

import { useDispatch } from 'react-redux';
import { MONTH_OR_YEAR } from 'config/AppConfig';
import { reduceCreateSubscription } from 'store/slices/subscription';
import { reduceUpdateSubscription } from 'store/slices/subscription';
import { fetchSubscriptionDetail } from 'store/slices/subscription';
import AppConfig from 'config/AppConfig';

const { PATTERNS } = AppConfig;
const useStyles = makeStyles(() => ({
  control: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: '1rem'
  }
}));

const SubscriptionPlanDetail = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { id: subscriptionId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [initializeData, setInitializeData] = useState({
    name: '',
    numberClient: '',
    numberWorker: '',
    pricing: '',
    recurring: 'MONTH',
    featureDescription: '',
    freePackage: ''
  });
  const isAdd = window.location.href.includes('create');

  const _loadData = async (subscriptionId) => {
    const result = await dispatch(fetchSubscriptionDetail({ id: subscriptionId }));
    const { payload } = result;
    if (payload) {
      const data = { ...initializeData };
      Object.keys(initializeData)
        .forEach(key => {
          if(key === 'name') {
            data[key] = payload['packageName'] || "";
          } else {
            data[key] = payload[key] || "";
          }
        });
      setInitializeData(data);
    } else {
      history.push('/admin/subscription-plan');
    }
  };

  const handleSubmitForm = async (data) => {
    setIsLoading(true);
    let result;
    if (isAdd) {
      result = await dispatch(reduceCreateSubscription({ 
        ...data,
        numberClient: parseInt(data.numberClient),
        numberWorker: parseInt(data.numberWorker)
      }));
    } else {
      result = await dispatch(reduceUpdateSubscription({ 
        ...data, 
        numberClient: parseInt(data.numberClient),
        numberWorker: parseInt(data.numberWorker),
        id: subscriptionId 
      }));
    }
    if (result.payload) {
      if(isAdd) {
        history.push(`/admin/subscription-plan`);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!isAdd && !!subscriptionId) {
      _loadData(subscriptionId);
    }
  }, [subscriptionId]);

  const validationSchema = Yup.object().shape({
    name: YupValidations.requiredString,
    numberClient: Yup.number().required('validation.required').min(1, { message: 'validation.minValue', value: 1 }).max(999999, { message: 'validation.maxValue', value: 999999 }).integer("validation.integerValue"),
    numberWorker: Yup.number().required('validation.required').min(1, { message: 'validation.minValue', value: 1 }).max(999999, { message: 'validation.maxValue', value: 999999 }).integer("validation.integerValue"),
    pricing: Yup.string().required('validation.required').matches(PATTERNS.PRICE_FORMAT, 'validation.pricing_format')
    .test('pricing', 'validation.maxValuePrice' , pricing => {
      if (parseFloat(pricing) > 99999) {
        return false;
      } else {
        return true;
      }
    })
    .test('pricing', 'validation.minValuePrice' , pricing => {
      if (parseInt(pricing) === 0) {
        return false;
      } else {
        return true;
      }
    }),
    recurring: Yup.string().required('validation.required'),
    featureDescription: Yup.string().required('validation.required').max(1000, { message: "validation.max_length", value: 1000 })
    .matches(PATTERNS.BLANK_SPACES, "validation.blankSpaces")
    // Yup.string().required('validation.required').min(1, { message: 'validation.minValue', value: 1 }).max(99999, { message: 'validation.maxValue', value: 99999 }).matches(PATTERNS.PRICE_FORMAT, 'validation.pricing_format'),
  });

  return (
    <React.Fragment>
      <Formik
        initialValues={initializeData}
        validationSchema={validationSchema}
        onSubmit={handleSubmitForm}
        enableReinitialize
      >
        {({values}) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  component={AppInput}
                  name="name"
                  label={t('label.subscription_name')}
                  required
                />
                <Field
                    component={AppInput}
                    name="numberWorker"
                    label={values.recurring === "YEAR" ? "label.number_of_workers_year" : "label.number_of_workers_month"}
                    type="number"
                    required
                />
                <Field
                    component={AppInput}
                    name="numberClient"
                    label={values.recurring === "YEAR" ? "label.number_of_clients_year" : "label.number_of_clients_month"}
                    type="number"
                    required
                />
                
                
              </Grid>
              <Grid item xs={12} md={6}>
              <Field
                  component={AppInput}
                  name="pricing"
                  label={"label.price"}
                  required
                />
                <Field
                  component={SelectField}
                  name="recurring"
                  label={t('label.recurring')}
                  options={MONTH_OR_YEAR.map(item => {
                    return {
                      value: item,
                      label: item === 'MONTH' ? t('label.month') : t('label.year')
                    }
                  })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <Field
                  component={AppInput}
                  name="featureDescription"
                  label={t('label.feature_list')}
                  type="textarea"
                  required
                />
              </Grid>
            </Grid>
            <FormGroup className={classes.control}>
            <AppButton
                className='ml-3'
                label={`${t("button.cancel")}`}
                rounder
                type="button"
                isLoading={isLoading}
                disabled={isLoading}
                onClick={() => history.push('/admin/subscription-plan')}
              />
              <AppButton
                className='ml-3'
                label={`${t("button.save")}`}
                rounder
                type="submit"
                isLoading={isLoading}
                disabled={isLoading || values.freePackage}
              />
            </FormGroup>
          </Form>
        )}
      </Formik>
    </React.Fragment>
  )
}

export default SubscriptionPlanDetail;
